import React, { useCallback, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';

import { ModalWrapper } from '@/components/wrappers';
import { RenewalCalendarEditPayload } from '@/modules/Renewals/renewals.types';

import PayrollCalendarDates from './PayrollCalendarDates/PayrollCalendarDates';
import { PayrollCalendarModalSteps } from './stores/useRenewalPayrollCalendarModal.store';
import RenewalPayrollCalendarParamsStep from './RenewalPayrollCalendarParamsStep';
import {
  useEditablePayrollCalendarStore,
  usePayrollCalendarDatesStore,
  usePayrollCalendarModalStore,
  usePayrollCalendarParamsStore,
} from './stores';

const MAX_MODAL_WIDTH = '1016px';
const DEFAULT_MONTHS = 17;

interface Props {
  onCalendarReady?: (payload: RenewalCalendarEditPayload, editableId?: string) => void;
}

export const RenewalPayrollCalendarModal: React.FC<Props> = ({ onCalendarReady }) => {
  const modalVisible = usePayrollCalendarModalStore((state) => state.modalVisible);
  const step = usePayrollCalendarModalStore((state) => state.step);
  const handleChangeVisibility = usePayrollCalendarModalStore((state) => state.handleChangeVisibility);
  const handleReset = usePayrollCalendarModalStore((state) => state.handleReset);
  const handleParamsReset = usePayrollCalendarParamsStore((state) => state.handleReset);
  const handleDatesReset = usePayrollCalendarDatesStore((state) => state.handleReset);
  const handleEditableReset = useEditablePayrollCalendarStore((state) => state.handleReset);
  const initialMonthDate = usePayrollCalendarDatesStore((state) => state.initialMonthDate);
  const dates = usePayrollCalendarDatesStore((state) => state.dates);
  const endDate = usePayrollCalendarDatesStore((state) => state.endDate);

  const monthRange: [string, string] = useMemo(() => {
    const start = dayjs(initialMonthDate).format('MMMM YYYY');
    const calculatedEndDate = endDate
      ? dayjs(endDate)
      : dayjs(dates[dates.length - 1]).isBefore(dayjs().add(DEFAULT_MONTHS, 'month'))
        ? dayjs().add(DEFAULT_MONTHS, 'month')
        : dayjs(dates[dates.length - 1]);
    const end = calculatedEndDate.format('MMMM YYYY');
    return [start, end];
  }, [initialMonthDate, dates, endDate]);

  const modalTitle = useMemo(() => {
    if (step === PayrollCalendarModalSteps.SET_PAYROLL_CALENDAR_DATES) {
      return `${monthRange[0]} - ${monthRange[1]}`;
    }
    return 'Edit Calendar';
  }, [monthRange, step]);

  const handleSetVisible = useCallback(() => {
    handleChangeVisibility(false);
  }, [handleChangeVisibility]);

  useEffect(() => {
    if (!modalVisible) {
      handleReset();
      handleEditableReset();
      handleParamsReset();
      handleDatesReset();
    }
  }, [handleEditableReset, handleReset, modalVisible, handleParamsReset, handleDatesReset]);

  return (
    <ModalWrapper
      testId="RenewalPayrollCalendarModal"
      visible={modalVisible}
      onSetVisible={handleSetVisible}
      title={modalTitle}
      titleBoxStyle={{ paddingBottom: '12px' }}
      maxWidth={step === PayrollCalendarModalSteps.SET_PAYROLL_CALENDAR_DATES ? MAX_MODAL_WIDTH : undefined}
      isClosingByClickOutsideBlocked
    >
      {step === PayrollCalendarModalSteps.SET_PAYROLL_CALENDAR_PARAMS && (
        <RenewalPayrollCalendarParamsStep onCancel={handleSetVisible} />
      )}
      {step === PayrollCalendarModalSteps.SET_PAYROLL_CALENDAR_DATES && (
        <PayrollCalendarDates onCalendarReady={onCalendarReady} onClose={handleSetVisible} />
      )}
    </ModalWrapper>
  );
};

export default RenewalPayrollCalendarModal;
